// standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
/**
 * Opt-in product events, the first-week progress record and station pricing
 * (ADR-0080; owner decisions 5 and 6, 2026-09-25). Renderer-safe: no
 * credential, identity or document data. The progress record is no longer
 * shown: the "Your first week" panel was removed (owner, 2026-10-03), and main
 * still reads its "second-batch" step for the usage funnel.
 */

/** Public station pricing. Checkout charges these amounts or refuses. */
export const capturePointStationPricing = Object.freeze({
  monthlyUsd: 190,
  yearlyUsd: 1_900,
  label: '$190 a month or $1,900 a year per station',
});

export type CheckoutPlan = 'monthly' | 'yearly';

/** A partner's customer discount, as Phone Home's partner-code check answers it: 1-40 percent. */
export type CapturePointCustomerDiscount = {
  readonly pct: number;
  readonly appliesTo: readonly string[];
};

/** The percent a discount takes off a Lite plan paid this way; 0 when it does not apply. */
export const liteDiscountPct = (
  discount: CapturePointCustomerDiscount | null | undefined,
  plan: CheckoutPlan,
): number =>
  discount && discount.appliesTo.includes(plan === 'yearly' ? 'lite-yearly' : 'lite-monthly')
    ? discount.pct
    : 0;

/** The only events an opted-in installation sends. Settings › License lists them. */
export const productEventCatalog = [
  { type: 'job_created', label: 'Job created', detail: 'when you create a job' },
  {
    type: 'setup_finished',
    label: 'Setup finished',
    detail: 'when a job finishes setting itself up',
  },
  {
    type: 'first_export',
    label: 'First export',
    detail: 'your first export, with the number of documents',
  },
  { type: 'training_run', label: 'Training run', detail: 'when you start training' },
  { type: 'pages_per_day', label: 'Pages per day', detail: 'how many pages were read each day' },
] as const;

export type ProductEventType = (typeof productEventCatalog)[number]['type'];

export type EngagementConsentState = 'unasked' | 'opted-in' | 'declined' | 'skipped';

export type FirstWeekStepId = 'create-job' | 'check-10' | 'export' | 'second-batch' | 'training';

export const firstWeekSteps: ReadonlyArray<{ id: FirstWeekStepId; label: string }> = [
  { id: 'create-job', label: 'Create a job' },
  { id: 'check-10', label: 'Check 10 documents' },
  { id: 'export', label: 'Export' },
  { id: 'second-batch', label: 'Add a second batch' },
  { id: 'training', label: 'Run training' },
];

/**
 * On-device milestone times (night goal 2026-09-25, Plan 1): how long this
 * computer took from first launch to its first job, confirmation and export.
 * Kept in main's per-install store; never sent anywhere.
 */
export const onboardingMilestones = [
  'firstLaunch',
  'firstJob',
  'firstConfirmation',
  'firstExport',
] as const;
export type OnboardingMilestone = (typeof onboardingMilestones)[number];
export const isOnboardingMilestone = (value: unknown): value is OnboardingMilestone =>
  typeof value === 'string' && (onboardingMilestones as readonly string[]).includes(value);

export type EngagementView = {
  consent: {
    state: EngagementConsentState;
    name: string | null;
    email: string | null;
    decidedUtc: string | null;
  };
  /** When this installation first opened with this feature; the checklist runs a week from it. */
  firstLaunchUtc: string;
  checklist: Array<{ id: FirstWeekStepId; label: string; doneUtc: string | null }>;
  /** Documents confirmed so far, for "Check 10 documents". */
  checkedDocuments: number;
  /** Events waiting to be sent, and the last successful send. */
  pendingEvents: number;
  lastSentUtc: string | null;
  /** When each first happened on this computer (UTC ISO); local only, never sent. */
  milestones: Partial<Record<OnboardingMilestone, string>>;
};

/**
 * `name` is optional (Phone Home takes the tips sign-up with only the email since 2026-10-07: the email
 * asks' "First name (optional)"). `source` "extra-week": the sign-up came with Add 7 free days.
 */
export type EngagementConsentInput =
  | { choice: 'opt-in'; name?: string; email: string; source?: 'extra-week' }
  | { choice: 'decline' }
  | { choice: 'skip' };

export type CheckoutStartInput = {
  plan: CheckoutPlan;
  companyName: string;
  contactName: string;
  email: string;
  phone?: string;
  /** Optional partner code, already confirmed by the live check. */
  partnerCode?: string;
  /**
   * Where the buyer came to the buy screen from (telemetry's `checkout.start` `from`; main checks it and sends
   * nothing else of it): the trial tray, Settings › License itself, the trial-ended banner, or a link.
   */
  from?: CheckoutFrom;
};

export type CheckoutFrom = 'tray' | 'license' | 'expired-gate' | 'link';

/**
 * "unavailable": Ademero has card payment closed (the only reason to switch the card button off).
 * "offline": a network, rate-limit or server hiccup: try again. "already-subscribed": this
 * computer already has a paid subscription.
 */
export type CheckoutStartResult =
  | { started: true; plan: CheckoutPlan; amountUsd: number }
  | {
      started: false;
      reason:
        | 'unavailable'
        | 'refused'
        | 'offline'
        | 'busy'
        | 'already-subscribed'
        | 'invalid-contact'
        | 'rate-limited';
      message: string;
    };

/**
 * Partner codes are four characters and never use the digits 0 or 1, so a
 * typed 0 or 1 can only mean O or I. Uppercase, trimmed, spaces and dashes
 * dropped; null when it cannot be a code (Phone Home applies the same rule).
 */
export function normalizePartnerCode(raw: string): string | null {
  const code = raw
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '')
    .replace(/0/g, 'O')
    .replace(/1/g, 'I');
  return /^[A-Z2-9]{4}$/.test(code) ? code : null;
}

/**
 * The live check answers only "accepted" or "not recognized", never whose code
 * it is, so the partner list can't be mined by guessing. A lockout after
 * repeated misses looks exactly like "not recognized".
 */
export type PartnerCodeLookupResult =
  | {
      accepted: true;
      code: string;
      /** The partner's page-pack discount, whole percent, when Phone Home answers one. */ discountPct?: number;
      /** The partner's customer discount and what it applies to ("lite-monthly", "lite-yearly", "pages-pack"). */
      customerDiscount?: CapturePointCustomerDiscount;
    }
  | { accepted: false; reason: 'not-recognized' | 'unavailable' };

/** How long the buy screen waits for the live partner-code check before saying it couldn't check (Phone Home checks it with the order). */
export const partnerCodeCheckDeadlineMs = 5_000;

/**
 * The live partner-code check, never longer than `deadlineMs`: past it (or on
 * any failure) it answers "unavailable", which never blocks Buy now or "Bill
 * us instead", so a slow network can't hold a purchase behind "Checking…".
 */
export function partnerLookupWithDeadline(
  lookup: (code: string) => Promise<PartnerCodeLookupResult>,
  code: string,
  deadlineMs: number = partnerCodeCheckDeadlineMs,
): Promise<PartnerCodeLookupResult> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve({ accepted: false, reason: 'unavailable' }), deadlineMs);
    lookup(code).then(
      (result) => {
        clearTimeout(timer);
        resolve(result);
      },
      () => {
        clearTimeout(timer);
        resolve({ accepted: false, reason: 'unavailable' });
      },
    );
  });
}

/** "Bill us instead": the same buyer, plan and partner code, as an invoice request staff approve. */
export type InvoiceRequestInput = CheckoutStartInput;

/**
 * `plan` is the plan of the request Ademero holds. `existing` means one was
 * already waiting: that request stands (a second one is not filed).
 */
export type InvoiceRequestResult =
  | {
      requested: true;
      plan: CheckoutPlan;
      amountUsd: number;
      existing: boolean;
      submittedUtc: string | null;
    }
  | {
      requested: false;
      reason:
        | 'unavailable'
        | 'refused'
        | 'offline'
        | 'busy'
        | 'already-subscribed'
        | 'invalid-contact'
        | 'rate-limited';
      message: string;
    };

/**
 * The invoice request this computer has waiting with Ademero, kept on this
 * computer until the license activates. `state: "denied"` once Ademero says
 * staff turned it down: the buy screen says so instead of "waiting".
 */
/** "paid-by-card": a card payment withdrew the request (never a refusal); the license follows the payment. */
export type PendingInvoiceRequest = {
  requestId: string;
  plan: CheckoutPlan;
  submittedUtc: string;
  state?: 'pending' | 'denied' | 'paid-by-card';
};

/**
 * The email rule Phone Home's purchase routes apply (zod 3's email check), so
 * an address accepted on this computer is never refused there. The buy screen
 * and main both use it: the field marker, the buttons and main agree.
 */
export const phoneHomeEmailPattern =
  /^(?!\.)(?!.*\.\.)([A-Z0-9_'+\-.]*)[A-Z0-9_+-]@([A-Z0-9][A-Z0-9-]*\.)+[A-Z]{2,}$/i;
/** The longest contact details Phone Home keeps. */
export const licenseContactLimits = Object.freeze({
  contactName: 200,
  companyName: 200,
  email: 254,
  phone: 40,
});
/** Main's refusals of contact details, written for the buyer: the buy screen shows them as they are. */
export const licenseContactMessages = Object.freeze({
  details: 'Check your contact details.',
  email: 'Enter a valid email address.',
  phone: `Keep the phone number to ${licenseContactLimits.phone} characters.`,
});

/** What is wrong with each contact field, in the words the form shows; empty when the details can be sent. */
export function licenseContactProblems(draft: {
  companyName: string;
  contactName: string;
  email: string;
  phone: string;
}): Partial<Record<'companyName' | 'contactName' | 'email' | 'phone', string>> {
  const problems: Partial<Record<'companyName' | 'contactName' | 'email' | 'phone', string>> = {};
  const name = draft.contactName.trim();
  const company = draft.companyName.trim();
  const email = draft.email.trim();
  if (!name) problems.contactName = 'Needed';
  else if (name.length > licenseContactLimits.contactName)
    problems.contactName = `Up to ${licenseContactLimits.contactName} characters`;
  if (!company) problems.companyName = 'Needed';
  else if (company.length > licenseContactLimits.companyName)
    problems.companyName = `Up to ${licenseContactLimits.companyName} characters`;
  if (!email) problems.email = 'Needed';
  else if (email.length > licenseContactLimits.email || !phoneHomeEmailPattern.test(email))
    problems.email = 'Check the address';
  if (draft.phone.trim().length > licenseContactLimits.phone)
    problems.phone = `Up to ${licenseContactLimits.phone} characters`;
  return problems;
}

/**
 * The message a failed purchase or request should show: main's own refusal of
 * the details when that is what happened (it says which detail to fix), else
 * the fallback. Electron wraps a main error as "Error invoking remote method
 * '<channel>': Error: <message>".
 */
export const licenseSupportEmail = 'support@ademero.com';

/** Phone Home said only support can recover this installation: say so, with the ID support needs. */
export function licenseSupportRecoveryMessage(installationId: string | null | undefined): string {
  return installationId
    ? `This computer's license needs support. Contact ${licenseSupportEmail} with installation ID ${installationId}.`
    : `This computer's license needs support. Contact ${licenseSupportEmail} with the installation ID shown on this screen.`;
}

export function buyerFacingLicenseError(error: unknown, fallback: string): string {
  const raw = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  if (raw.includes('capturepoint_v6_license_support-recovery-required'))
    return licenseSupportRecoveryMessage(null);
  const message = raw.replace(/^[\s\S]*Error: /, '').trim();
  const known: readonly string[] = Object.values(licenseContactMessages);
  return known.includes(message) ? message : fallback;
}

/** Which details a click could not send with, named, so a click with details missing always says which. */
export function licenseContactProblemSummary(
  problems: Partial<Record<'companyName' | 'contactName' | 'email' | 'phone', string>>,
): string | null {
  const names = {
    contactName: 'your name',
    email: 'the email address',
    companyName: 'the company',
    phone: 'the phone number',
  } as const;
  const listed = (['contactName', 'email', 'companyName', 'phone'] as const)
    .filter((key) => problems[key])
    .map((key) => names[key]);
  if (listed.length === 0) return null;
  const text =
    listed.length === 1 ? listed[0]! : `${listed.slice(0, -1).join(', ')} and ${listed.at(-1)}`;
  return `Check ${text}.`;
}

/** A card checkout opened in the browser: the buy screen keeps saying so while main waits for the payment (45 minutes). */
export const openCheckoutShownMs = 45 * 60_000;
export function openCheckoutMessage(
  open: { plan: CheckoutPlan; openedUtc: string } | null,
  nowMs: number,
): string | null {
  if (!open) return null;
  const opened = Date.parse(open.openedUtc);
  if (!Number.isFinite(opened) || nowMs - opened > openCheckoutShownMs || nowMs < opened - 60_000)
    return null;
  const price =
    open.plan === 'yearly'
      ? `$${capturePointStationPricing.yearlyUsd.toLocaleString('en-US')} a year`
      : `$${capturePointStationPricing.monthlyUsd} a month`;
  const at = new Date(opened).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `Checkout open in your browser since ${at} (${price}). After payment this computer activates on its own.`;
}

/**
 * Whether a license broadcast changes what the buy screen's last message is
 * about. Main publishes on every refresh (and refreshes every 20 s while a
 * checkout is open), so an unchanged license must not wipe a confirmation or
 * a problem the buyer still has to act on.
 */
export function licenseBroadcastClearsMessages(
  previous: {
    runtimeState?: string;
    activationRequestState?: string | null;
    salesInquiryState?: string | null;
    moreTimeRequest?: { state?: string } | null;
  } | null,
  next: {
    runtimeState?: string;
    activationRequestState?: string | null;
    salesInquiryState?: string | null;
    moreTimeRequest?: { state?: string } | null;
  },
): boolean {
  // The first license read (after a failed one) replaces whatever was said about reading it.
  if (previous === null) return true;
  return (
    previous.runtimeState !== next.runtimeState ||
    (previous.activationRequestState ?? null) !== (next.activationRequestState ?? null) ||
    (previous.salesInquiryState ?? null) !== (next.salesInquiryState ?? null) ||
    (previous.moreTimeRequest?.state ?? null) !== (next.moreTimeRequest?.state ?? null)
  );
}

export function validateConsentInput(value: unknown): EngagementConsentInput {
  const input = value as EngagementConsentInput | null;
  if (input?.choice === 'decline' || input?.choice === 'skip') return { choice: input.choice };
  if (input?.choice !== 'opt-in') throw new Error('Choose whether to get setup tips.');
  const name = typeof input.name === 'string' ? input.name.trim() : '';
  const email = typeof input.email === 'string' ? input.email.trim() : '';
  if (name.length > 200 || /[\r\n\u0000]/.test(name)) throw new Error('Enter your name.');
  // Phone Home's own rule: an address it would refuse is never kept here (a refusal looks like being offline, so the
  // sign-up and every event after it would be retried and never delivered).
  if (email.length > licenseContactLimits.email || !phoneHomeEmailPattern.test(email))
    throw new Error('Enter a valid work email.');
  if (input.source !== undefined && input.source !== 'extra-week')
    throw new Error('Choose whether to get setup tips.');
  return {
    choice: 'opt-in',
    ...(name ? { name } : {}),
    email,
    ...(input.source ? { source: input.source } : {}),
  };
}

/** Whole days and hours left, for the countdown shown outside Settings. */
export function trialCountdownLabel(secondsRemaining: number): string {
  if (secondsRemaining <= 0) return 'Trial ended';
  const hours = Math.ceil(secondsRemaining / 3_600);
  if (hours <= 24)
    return hours === 1 ? '1 hour left in your trial' : `${hours} hours left in your trial`;
  const days = Math.ceil(secondsRemaining / 86_400);
  return days === 1 ? '1 day left in your trial' : `${days} days left in your trial`;
}

/**
 * The countdown's state as telemetry names it (`trial.tray-shown`): ended, the last day (24 hours or less, as
 * the countdown counts hours) or days left, with the hours left as a bucket.
 */
export function trialTrayState(secondsRemaining: number): {
  state: 'n-days' | 'last-day' | 'ended';
  hoursLeft: '0' | '1-24' | '25-48' | '49-72' | '72+';
} {
  if (secondsRemaining <= 0) return { state: 'ended', hoursLeft: '0' };
  const hours = Math.ceil(secondsRemaining / 3_600);
  return {
    state: hours <= 24 ? 'last-day' : 'n-days',
    hoursLeft: hours <= 24 ? '1-24' : hours <= 48 ? '25-48' : hours <= 72 ? '49-72' : '72+',
  };
}

/** The countdown and the free extension appear only near the end: the last 3 days. */
export const trialNearEndSeconds = 3 * 86_400;

/**
 * Which trial offers show (owner trial rule, 2026-09-25): the one free 7-day
 * extension only in the last 3 days of the first week or after it ended; once
 * it is used, a staff-reviewed request for more time, one open at a time.
 */
export function trialOffers(
  license: {
    readonly runtimeState: string;
    readonly secondsRemaining: number | null;
    readonly canAddTrialDays: boolean;
    readonly canAskForMoreTime?: boolean;
    readonly moreTimeRequest?: { readonly state: 'pending' | 'approved' | 'denied' } | null;
  } | null,
): { nearEnd: boolean; freeExtension: boolean; askForMoreTime: boolean; moreTimePending: boolean } {
  const nearEnd =
    license?.runtimeState === 'trial-expired' ||
    (license?.runtimeState === 'trial-active' &&
      license.secondsRemaining !== null &&
      license.secondsRemaining <= trialNearEndSeconds);
  return {
    nearEnd,
    freeExtension: nearEnd && license?.canAddTrialDays === true,
    askForMoreTime:
      nearEnd && license?.canAddTrialDays === false && license.canAskForMoreTime === true,
    moreTimePending: nearEnd && license?.moreTimeRequest?.state === 'pending',
  };
}
